import { useContext, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Styles from "./styles.module.scss";

import { TranslationContext } from "../../../../contexts/translation/index.context";
import { TableContext } from "../../../../contexts/table/index.context";
import { MergedColumnType } from "./types";
//Columns
import {
  incomingColumns,
  outcomingInternalColumns,
  resolutionsColumns,
  listOfDocumentsColumns,
  passingColumns,
  relatedDocumentsColumns,
  discussionColumns,
  electronicMailColumns,
  electronicMailFilesColumns,
  incomingEmailColumns,
} from "./columns/documents";
import {
  actionsWithDocumentsColumns,
  nomenclatureOfCasesCurrentColumns,
  nomenclatureOfCasesColumns,
  resolutionTemplatesColumns,
  executorsColumns,
  resultsOfActionsColumns,
  documentCharColumns,
  typesOfReceiptsColumns,
} from "./columns/directories/document-characteristics";
import {
  professionClassifierColumns,
  jobCategoriesColumns,
  typesOfStructuralUnitColumns,
  employeesColumns,
  structuralUnitsColumns,
  positionsColumns,
  employeesAndContactPersonsColumns,
  departmentalJournalsColumns,
  groupsOfCorrespondentsColumns,
} from "./columns/directories/hr-department";
import { correspondentsColumns,  correspondentsColumnsOutcoming} from "./columns/directories/general";
import {
  configuringPageAccessColumns,
  buttonAccessSettingsColumns,
  serverSettingsColumns,
  usersColumns,
  documentDisplayOptionsColumns,
  documentDisplayOptionsAccessColumns,
  barcodeApplicationOptionsColumns,
  mailboxesColumns,
  mailboxesByUserColumns,
} from "./columns/administration";
import { menuTabsColumns } from "./columns/profile";
//UI
import type { InputRef } from "antd/es/input";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import Button from "antd/lib/button";
import Input from "antd/lib/input";
import Space from "antd/lib/space";
import Tooltip from "antd/lib/tooltip";
import Highlighter from "react-highlight-words";
//Icons
import SearchOutlined from "@ant-design/icons/SearchOutlined";

export function useTableColumns(
  reset: boolean,
  setReset: React.Dispatch<React.SetStateAction<boolean>>,
  url: string | undefined,
  tableData: MergedColumnType[],
  secondTable?: boolean,
  onChange?: (id: number, e: boolean) => Promise<void>
) {
  type DataIndex = keyof MergedColumnType;
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  const { intl } = useContext(TranslationContext);
  const { visibleColumn } = useContext(TableContext);
  const location = useLocation();

  const filters = [
    { text: intl("marked"), value: true },
    { text: intl("unmarked"), value: false },
  ];

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (
    clearFilters: () => void,
    confirm: (param?: FilterConfirmProps) => void
  ) => {
    console.log("clearFilters()");
    clearFilters();
    confirm({ closeDropdown: false });
    setSearchText("");
    setSearchedColumn("");
  };

  const handleResetAll = () => {
    setSearchText("");
    setSearchedColumn("");
    setReset(true);
    reset && setReset(false);
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<MergedColumnType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          className={Styles["custom-input"]}
          ref={searchInput}
          placeholder={`${intl("search")}...`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            // type="default"
            className={Styles["custom-button"]}
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            // style={{ width: 90 }}
          >
            {intl("search")}
          </Button>
          <Button
            className={Styles["custom-button"]}
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            {intl("reset")}
          </Button>
          <Button 
            size="small"
            className={Styles["custom-button"]}
            onClick={handleResetAll}>
            {intl("reset-all")}
          </Button>
          <Button
            className={Styles["custom-button"]}
            // type="link"
            size="small"
            onClick={() => {
              setSelectedKeys(["пусті"]);
              handleSearch(["empty"], confirm, dataIndex);
            }}
          >
            {intl("search-empty")}
          </Button>
          {/* <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            {intl("filter")}
          </Button> */}
          <Button
            className={Styles["custom-button"]}
            // type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            {intl("close")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      // <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      <SearchOutlined style={{ color: filtered ? "#fff" : undefined }} />

    ),
    onFilter: (value, record) => {
      return value === "пусті"
        ? record[dataIndex] === null || record[dataIndex] === ""
        : record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Tooltip title={text}>
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        </Tooltip>
      ) : (
        text
      ),
  });

  type ColumnFunction = (
    intl: (id: string) => string,
    getColumnSearchProps?: (
      dataIndex: keyof MergedColumnType
    ) => ColumnType<MergedColumnType>,
    filters?: { text: string; value: boolean }[],
    data?: MergedColumnType[],
    visible?: boolean,
    onChange?: (id: number, e: boolean) => Promise<void>,
  ) => ColumnsType<MergedColumnType>;

  const columnFunctions: Record<string, ColumnFunction> = {
    // DOCUMENTS
    "/documents/incoming": incomingColumns,
    "/documents/outcoming": outcomingInternalColumns,
    "/documents/internal": outcomingInternalColumns,
    "/documents/resolutions": resolutionsColumns,
    "/documents/for-signature": resolutionsColumns,
    "/documents/list-of-documents": listOfDocumentsColumns,
    "/documents/electronic-mail": electronicMailColumns,
    "/documents/electronic-mail/getfiles": electronicMailFilesColumns,
    "/documents/:page": incomingColumns,
    // DOCUMENT CHARACTERISTICS
    "/directories/actions-with-documents": actionsWithDocumentsColumns,
    "/directories/nomenclature-of-cases-current":
      nomenclatureOfCasesCurrentColumns,
    "/directories/nomenclature-of-cases": nomenclatureOfCasesColumns,
    "/directories/resolution-templates": resolutionTemplatesColumns,
    "/directories/resolution-templates/executors": executorsColumns,
    "/directories/types-of-documents": documentCharColumns,
    "/directories/indexes-of-documents": documentCharColumns,
    "/directories/types-of-deliveries": documentCharColumns,
    "/directories/types-of-communication": documentCharColumns,
    "/directories/results-of-actions": resultsOfActionsColumns,
    "/directories/document-categories": documentCharColumns,
    "/directories/types-of-relationships-between-documents":
      documentCharColumns,
    "/directories/types-of-receipts": typesOfReceiptsColumns,

    // HR DEPARTMENT
    "/directories/profession-classifier": professionClassifierColumns,
    "/directories/job-categories": jobCategoriesColumns,
    "/directories/types-of-structural-unit": typesOfStructuralUnitColumns,
    "/directories/employees": employeesColumns,
    "/directories/structural-units": structuralUnitsColumns,
    "/directories/positions": positionsColumns,
    "/directories/employees-and-contact-persons":
      employeesAndContactPersonsColumns,
    "/directories/departmental-journals": departmentalJournalsColumns,
    "/directories/groups-of-correspondents": groupsOfCorrespondentsColumns,
    // GENERAL
    "/directories/correspondents": correspondentsColumns,
    // OutcomingCorrespondents
    "/directories/outcoming-correspondents": correspondentsColumnsOutcoming,

    // OTHER DIRECTORIES
    "/directories/passing": passingColumns,
    "/directories/related-documents": relatedDocumentsColumns,
    "/directories/discussion": discussionColumns,
    "/directories/incoming-email": incomingEmailColumns,
    // ADMINISTRATION
    "/administration/configuring-page-access": configuringPageAccessColumns,
    "/administration/button-access-settings": buttonAccessSettingsColumns,
    "/administration/server-settings": serverSettingsColumns,
    "/administration/users": usersColumns,
    "/administration/document-display-options": secondTable
      ? documentDisplayOptionsAccessColumns
      : documentDisplayOptionsColumns,
    "/administration/barcode-application-options":
      barcodeApplicationOptionsColumns,
    "/administration/mailboxes": mailboxesColumns,
    "/administration/mailboxes-by-user": mailboxesByUserColumns,
    // PROFILE
    "/profile/menu-tabs": menuTabsColumns,
  };

  const resolveColumnFunction = (path: string): ColumnFunction | undefined => {
    return (
      columnFunctions[path] ||
      (path.startsWith("/documents/") ? incomingColumns : undefined)
    );
  };

  // const getColumnFunction = columnFunctions[url || location.pathname];
  const getColumnFunction = resolveColumnFunction(url || location.pathname);

  let columns: ColumnsType<MergedColumnType> = [];

  if (getColumnFunction) {
    columns = getColumnFunction(
      intl,
      getColumnSearchProps,
      filters,
      tableData,
      visibleColumn,
      onChange
    );
  }

  return columns;
}
